<template>
  <v-chart :option="option" autoresize/>
</template>

<script>
import {use} from 'echarts/core';
import {CanvasRenderer} from 'echarts/renderers';
import {BarChart, LineChart} from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  DataZoomComponent,
} from 'echarts/components';
import VChart from 'vue-echarts';
import {ref, defineComponent, computed} from 'vue';
import i18n from "../../lang/i18n";

use([
  CanvasRenderer,
  BarChart,
  LineChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  DataZoomComponent,
]);

export default defineComponent({
  components: {
    VChart,
  },
  props: ["chartData", "locale"],
  provide: {},
  methods: {},
  setup(props) {

    function fixOverflowBarChart(list, value) {
      return [value].concat(list).concat([value])
    }

    function averageList(average, count) {
      let list = []
      for (let i = 0; i < count; i++) list.push(average)
      return list
    }

    const option = computed(() => {
      let locale = props.locale
      let cd = props.chartData

      let data1 = cd.data1[0]
      let data2 = cd.data1[1]
      let data3 = cd.data1[2]
      let average1 = cd.data2[0]
      let average2 = cd.data2[1]
      let average3 = cd.data2[2]

      let data = [
        fixOverflowBarChart(data1, 0),
        fixOverflowBarChart(data2, 0),
        fixOverflowBarChart(data3, 0),
      ]
      let averages = [
        averageList(average1, data1.length + 2),
        averageList(average2, data2.length + 2),
        averageList(average3, data3.length + 2),
      ]

      let dataLegends = locale === 'en' ? cd.legend2En : cd.legend2Vi
      let averageLegends = locale === 'en' ? cd.legend1En : cd.legend1Vi
      let xAxis = fixOverflowBarChart(locale === 'en' ? cd.en : cd.vi, '')

      // Fix data length to match xAxis labels length
      const adjustedData = data.map(series => {
        const adjustedSeries = [...series]
        // Extend with zeros if data is shorter than labels
        while (adjustedSeries.length < xAxis.length) {
          adjustedSeries.push(0)
        }
        // Trim if data is longer than labels
        return adjustedSeries.slice(0, xAxis.length)
      })
      data = adjustedData

      return {
        color: cd.colors1,
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross'
          }
        },
        grid: {
          left: '3%',
          right: '3%',
          bottom: xAxis.length > 4 ? '25%' : '15%',
          top: '20%',
          containLabel: true
        },
        dataZoom: xAxis.length > 4 ? [
          {
            type: 'inside',
            xAxisIndex: [0],
            start: 0,
            end: Math.min(100, Math.round(600 / xAxis.length)),
            zoomOnMouseWheel: true,
            moveOnMouseMove: true,
            moveOnMouseWheel: true
          },
          {
            type: 'slider',
            xAxisIndex: [0],
            start: 0,
            end: Math.min(100, Math.round(600 / xAxis.length)),
            bottom: '5%',
            height: '20px',
            showDetail: false,
            handleStyle: {
              color: '#1890FF',
              borderColor: '#1890FF'
            }
          }
        ] : [],
        legend: [
          {
            right: '3%',
            data: averageLegends
          },
          {
            right: '3%',
            top: 24,
            data: dataLegends
          },
        ],
        xAxis: [
          {
            type: 'category',
            axisTick: {
              alignWithLabel: true
            },
            axisLabel: {
              interval: 0,
              hideOverlap: true,
              rotate: 45,
              fontSize: 10,
              formatter: function (value) {
                // Truncate long labels and add ellipsis
                if (value.length > 15) {
                  return value.substring(0, 15) + '...'
                }
                return value
              }
            },
            boundaryGap: false,
            data: xAxis,
          }
        ],
        yAxis: [
          {
            type: 'value',
            name: i18n.t('ui.secondWrapped'),
            nameTextStyle: {
              fontFamily: 'roboto',
            },
            position: 'left',
            alignTicks: true,
            axisLine: {
              show: true,
              // lineStyle: {
              //   color: colors[0]
              // }
            },
            // axisLabel: {
            //   formatter: '{value} ml'
            // }
          },
          {
            type: 'value',
            name: i18n.t('ui.numberCallWrapped'),
            nameTextStyle: {
              fontFamily: 'roboto',
            },
            position: 'right',
            alignTicks: true,
            // max: 30,
            // min: 0,
            axisLine: {
              show: true,
              // lineStyle: {
              //   color: colors[2]
              // }
            },
            // axisLabel: {
            //   formatter: '{value} °C'
            // }
          }
        ],
        series: [
          {
            type: 'line',
            yAxisIndex: 1,
            showSymbol: false,
            areaStyle: {
              color: cd.colors2[0]
            },
            lineStyle: {
              width: 2,
              type: 'dashed',
              color: cd.colors1[0],
            },
            name: averageLegends[0],
            data: averages[0],
          },
          {
            type: 'line',
            yAxisIndex: 1,
            showSymbol: false,
            areaStyle: {
              color: cd.colors2[1]
            },
            lineStyle: {
              width: 2,
              type: 'dashed',
              color: cd.colors1[1],
            },
            name: averageLegends[1],
            data: averages[1],
          },
          {
            type: 'line',
            showSymbol: false,
            areaStyle: {
              color: cd.colors2[2]
            },
            lineStyle: {
              width: 2,
              type: 'dashed',
              color: cd.colors1[2],
            },
            name: averageLegends[2],
            data: averages[2],
          },
          {
            barGap: 0,
            barCategoryGap: '40%',
            type: 'bar',
            yAxisIndex: 1,
            stack: "call",
            name: dataLegends[0],
            data: data[0]
          },
          {
            type: 'bar',
            yAxisIndex: 1,
            stack: "call",
            name: dataLegends[1],
            data: data[1]
          },
          {
            type: 'bar',
            name: dataLegends[2],
            data: data[2]
          },
        ]
      }
    });

    return {option};
  },
});


</script>

<style scoped>
</style>